import * as THREE from "three";
import { getHatchTextureMaterialFromThreeMaterial } from "../../materials/designMaterialHatchTexture";
import { EffectComposerPass } from "../examples/jsm/postprocessing/EffectComposerPass";
import { FullScreenQuad } from "../examples/jsm/postprocessing/FullScreenQuad";
import type { RenderTargetPool } from "./RenderTargetPool";
import { SSFXRendererOutput } from "./SSFXRendererOutput";
import type SSFXRendererScene from "./SSFXRendererScene";

const transparentMaterial = new THREE.MeshBasicMaterial({
  transparent: true,
  opacity: 0,
  name: "HatchMaterialPass transparentMaterial",
});

export class HatchMaterialPass extends EffectComposerPass {
  camera: THREE.Camera;
  scene: SSFXRendererScene | undefined;

  clear: boolean = false;
  output: SSFXRendererOutput = SSFXRendererOutput.LINEWORK;

  private overrideMaterials = new WeakMap<THREE.Material, THREE.Material>();

  // Used in linework composition.
  private fsQuad: FullScreenQuad;
  private fsQuadMaterial: THREE.MeshBasicMaterial;

  constructor(
    scene: SSFXRendererScene | undefined,
    camera: THREE.Camera,
    private renderTargetPool: RenderTargetPool,
  ) {
    super();
    this.scene = scene;
    this.camera = camera;

    this.fsQuadMaterial = new THREE.MeshBasicMaterial({
      transparent: true,
      blending: THREE.NormalBlending,
      name: "HatchMaterialPass fsQuadMaterial",
    });
    this.fsQuad = new FullScreenQuad(this.fsQuadMaterial);
  }

  updateScene(hatchThreshold: number): void {
    if (!this.scene) {
      return;
    }
    this.overrideMaterials = new WeakMap();
    this.scene.scene.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        const materialOverride = obj.material.userData.materialParams
          ? getHatchTextureMaterialFromThreeMaterial(obj.material, hatchThreshold)
          : transparentMaterial;
        this.overrideMaterials.set(obj.material, materialOverride);
      }
    });
  }

  render(renderer: THREE.WebGLRenderer, writeBuffer: THREE.WebGLRenderTarget | null): void {
    if (!this.scene || !this.camera) {
      return;
    }

    const skybox = this.scene.sceneLightsManager.skybox;
    const oldSkyboxVisible = skybox.visible;

    // Disable the skybox to match PDF.
    skybox.visible = false;

    const oldClearColor = new THREE.Color("white");
    renderer.getClearColor(oldClearColor);
    renderer.setClearColor("white");
    const oldClearAlpha = renderer.getClearAlpha();
    renderer.setClearAlpha(0);

    this.scene.scene.overrideMaterial = this.overrideMaterials as any; // Using our three.js override materials patch

    // This clear prevents over-draw of the same hatching, giving excessively dark lines.
    const intermediateBuffer = this.renderTargetPool.reserveRenderTargetMatching(writeBuffer, { depthBuffer: true });
    renderer.setRenderTarget(intermediateBuffer);
    renderer.clear(true, true, false);
    // Ignore the clear color in the scene background by temporarily setting it to null.
    const oldBackground = this.scene.scene.background;
    this.scene.scene.background = null;
    renderer.render(this.scene.scene, this.camera);

    skybox.visible = oldSkyboxVisible;
    this.scene.scene.background = oldBackground;
    this.scene.scene.overrideMaterial = null;

    renderer.setRenderTarget(writeBuffer);

    if (this.clear) {
      if (this.output === SSFXRendererOutput.LINEWORK_TRANSPARENT) {
        renderer.setClearAlpha(0);
      } else {
        renderer.setClearAlpha(1);
      }
      renderer.clear(true, true, false);
    }

    this.fsQuadMaterial.map = intermediateBuffer.texture;
    this.fsQuad.render(renderer);

    this.renderTargetPool.freeRenderTarget(intermediateBuffer);

    renderer.setClearColor(oldClearColor);
    renderer.setClearAlpha(oldClearAlpha);
  }
}
